
import {
  CareerSiteLocationResponse,
  CareerSitePositionResponse,
  CareerSiteService,
} from 'api-clients/monolith';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePositionsCache } from './usePositionsCache';

// Global cache shared across all usePositions instances
const globalPositionsCache = new Map<
  string,
  {
    data: CareerSitePositionResponse['positions'];
    pagination: CareerSiteLocationResponse['pagination'];
  }
>();

interface UsePositionsParams {
  accountSlug: string;
  brandId: string;
  query?: string;
  perPage?: number;
  locale?: string;
}

interface UsePositionsReturn {
  data: CareerSitePositionResponse['positions'];
  isFetching: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  pagination?: CareerSitePositionResponse['pagination'];
  addPositionToCache: (position: { id: string; name: string }) => void;
}

export function usePositions({
  accountSlug,
  brandId,
  query = '',
  perPage = 50,
  locale,
}: UsePositionsParams): UsePositionsReturn {
  const [data, setData] = useState<CareerSitePositionResponse['positions']>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] =
    useState<CareerSitePositionResponse['pagination']>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  const { clearCache } = usePositionsCache();

  // Memoize the cache key to prevent unnecessary refetches
  const cacheKey = useMemo(() => {
    return `${accountSlug}-${brandId}-${query}-${perPage}-${locale ?? ''}`;
  }, [accountSlug, brandId, query, perPage, locale]);

  const fetchData = useCallback(
    async (pageNum: number, searchQuery: string, reset = false) => {
      // Check global cache first for the first page
      if (pageNum === 1 && reset) {
        const cachedData = globalPositionsCache.get(cacheKey);
        if (cachedData) {
          setData(cachedData.data);
          setPagination(cachedData.pagination);
          return;
        }
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      abortControllerRef.current = new AbortController();

      setIsFetching(true);

      try {
        const request = CareerSiteService.getInternalApiCareerSitePositions(
          {
            account_slug: accountSlug,
            brand_id: brandId,
          },
          searchQuery.trim() || undefined,
          pageNum,
          perPage,
          undefined,
          locale,
        );

        const abortListener = () => request.cancel();
        abortControllerRef.current.signal.addEventListener(
          'abort',
          abortListener,
        );

        const result = (await request) as CareerSitePositionResponse;

        abortControllerRef.current?.signal.removeEventListener(
          'abort',
          abortListener,
        );

        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        setPagination(result.pagination);

        if (reset) {
          setData(result.positions);
          // Cache the first page data in global cache
          if (pageNum === 1) {
            globalPositionsCache.set(cacheKey, {
              data: result.positions,
              pagination: result.pagination,
            });
          }
        } else {
          setData(prev => [...prev, ...result.positions]);
        }
      } catch {
        // fetch error — not re-thrown
      } finally {
        if (isMountedRef.current) {
          setIsFetching(false);
        }
      }
    },
    [accountSlug, brandId, perPage, locale, cacheKey],
  );

  const loadMore = useCallback(() => {
    if (!isFetching && pagination && currentPage < pagination.total_pages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      void fetchData(nextPage, query, false);
    }
  }, [currentPage, fetchData, isFetching, pagination, query]);

  const refetch = useCallback(() => {
    setCurrentPage(1);
    void fetchData(1, query, true);
  }, [fetchData, query]);

  const hasMore = pagination ? currentPage < pagination.total_pages : false;

  const addPositionToCache = useCallback(
    (position: { id: string; name: string }) => {
      // Add to current data if not already present
      setData(prev => {
        if (prev.find(pos => pos.id === position.id)) {
          return prev;
        }
        return [
          {
            id: position.id,
            name: position.name,
            count: 0, // Default values for missing properties
            title: position.name,
          },
          ...prev,
        ];
      });
    },
    [],
  );

  useEffect(() => {
    setCurrentPage(1);
    void fetchData(1, query, true);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query, fetchData]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Clear cache when locale changes
  useEffect(() => {
    clearCache();
    globalPositionsCache.clear();
  }, [locale, clearCache]);

  return {
    data,
    isFetching,
    hasMore,
    loadMore,
    refetch,
    pagination,
    addPositionToCache,
  };
}
